import React, { useState, useEffect } from 'react';
import { fetchPayouts } from '../services/payoutService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';

const Payouts = () => {
  const { token } = useAuth();
  const { t } = useLanguage(); // eslint-disable-line no-unused-vars
  const [payouts, setPayouts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchPayouts(token)
      .then(setPayouts)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleApprove = (id) => {
    // TODO: Implement approve API call
    setPayouts(payouts.map((p) => (p.id === id ? { ...p, status: 'approved' } : p)));
  };

  const handleReject = (id) => {
    // TODO: Implement reject API call
    setPayouts(payouts.map((p) => (p.id === id ? { ...p, status: 'rejected' } : p)));
  };

  const filteredPayouts = payouts.filter(
    (p) =>
      p.merchant_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.status?.toLowerCase().includes(search.toLowerCase())
  );

  const statusVariant = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'destructive';
    return 'warning';
  };

  return (
    <div>
      <PageHeader title="Payouts" />
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="text"
          placeholder="Search by merchant or status..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-64"
        />
      </div>
      {loading ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={6} />
        </div>
      ) : error ? (
        <div className="py-8 text-center text-destructive">{error}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPayouts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-6 text-center text-muted-foreground">
                      No payouts found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPayouts.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.id}</TableCell>
                      <TableCell>{p.merchant_name || '-'}</TableCell>
                      <TableCell>{p.amount}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(p.status)}>{p.status}</Badge>
                      </TableCell>
                      <TableCell>{p.date ? new Date(p.date).toLocaleString() : '-'}</TableCell>
                      <TableCell>
                        {p.status === 'pending' && (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleApprove(p.id)}>
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleReject(p.id)}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Payouts;
